import type { ApiMessage } from '@xtreed/academy-shared';
import { api } from './api';

export interface Certificate {
  id: string;
  userId: string;
  productId: string;
  templateId?: string;
  code: string;
  productName: string;
  userName: string;
  issuedAt: string;
  completedAt: string;
  pdfUrl?: string;
  metadata?: Record<string, unknown>;
  product?: {
    id: string;
    title: string;
    slug: string;
    thumbnail?: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CertificateTemplate {
  id: string;
  name: string;
  description?: string;
  htmlTemplate: string;
  cssStyles?: string;
  variables?: string[];
  isDefault: boolean;
  isActive?: boolean;
}

export interface VerificationResult extends Partial<ApiMessage> {
  valid: boolean;
  certificate?: {
    id: string;
    studentName: string;
    productName: string;
    issuedAt: string;
    code: string;
  };
}

class CertificatesService {
  // User methods
  async getMyCertificates(): Promise<Certificate[]> {
    const { data } = await api.get('/certificates/my');
    return data.data || data;
  }

  async generateMyCertificate(productId: string): Promise<Certificate> {
    const { data } = await api.post(`/certificates/my/generate/${productId}`);
    return data;
  }

  async getCertificateById(id: string): Promise<Certificate> {
    const { data } = await api.get(`/certificates/${id}`);
    return data;
  }

  async getDownloadUrl(id: string): Promise<{ url: string }> {
    const { data } = await api.get(`/certificates/${id}/download`);
    return data.data || data;
  }

  getCertificateHtmlUrl(id: string): string {
    return `${api.defaults.baseURL}/certificates/${id}/html`;
  }

  // Public verification
  async verifyCertificate(code: string): Promise<VerificationResult> {
    const { data } = await api.get(`/certificates/verify/${code}`);
    return data;
  }

  // Admin methods
  async findAll(
    params: {
      page?: number;
      limit?: number;
      productId?: string;
      userId?: string;
    } = {},
  ): Promise<{
    data: Certificate[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const { data } = await api.get(
      `/certificates/admin?${searchParams.toString()}`,
    );
    return data;
  }

  async generateCertificateForUser(
    productId: string,
    userId: string,
    templateId?: string,
  ): Promise<Certificate> {
    const { data } = await api.post(`/certificates/generate/${productId}`, {
      userId,
      templateId,
    });
    return data;
  }

  // Template methods
  async getTemplates(
    params: {
      page?: number;
      limit?: number;
    } = {},
  ): Promise<{
    data: CertificateTemplate[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const { data } = await api.get(
      `/certificates/templates?${searchParams.toString()}`,
    );
    return data;
  }

  async getTemplateById(id: string): Promise<CertificateTemplate> {
    const { data } = await api.get(`/certificates/templates/${id}`);
    return data;
  }

  async createTemplate(
    template: Partial<CertificateTemplate>,
  ): Promise<CertificateTemplate> {
    const { data } = await api.post('/certificates/templates', template);
    return data;
  }

  async updateTemplate(
    id: string,
    template: Partial<CertificateTemplate>,
  ): Promise<CertificateTemplate> {
    const { data } = await api.put(`/certificates/templates/${id}`, template);
    return data;
  }

  async deleteTemplate(id: string): Promise<void> {
    await api.delete(`/certificates/templates/${id}`);
  }

  getTemplatePreviewUrl(id: string): string {
    return `${api.defaults.baseURL}/certificates/templates/${id}/preview`;
  }
}

export const certificatesService = new CertificatesService();
